import React from 'react';

import { Box, Grid } from '@mui/material';

import { Offer, OfferCategory } from '../../../graphql/API';
import { CollabReducer } from '../../../pages/Collabs/reducer';
import { markHighlight } from '../../../redux/features/invite/inviteSlice';
import { useAppDispatch } from '../../../redux/hooks';
import { useCreateInvite } from '../../Dialogs/CreateInvite/context/CreateInvite.context';
import CreateInviteProvider, {
  CreateInviteProviderProps,
} from '../../Dialogs/CreateInvite/context/CreateInvite.provider';
import { EventStep, Step } from '../../Dialogs/CreateInvite/context/types';
import { Confirmation } from './Steps/Confirmation';
import { RequirementStep } from './Steps/RequirementStep';
import { Step1 } from './Steps/Step1';
import { Step2 } from './Steps/Step2';
import { Step3 } from './Steps/Step3';
import { SentSuccessfully } from './components/SentSuccessfully';

type Props = {
  reducer: CollabReducer;
  handleUserDeletion?: CreateInviteProviderProps['handleUserDeletion'];
  onBack?: () => void;
};

type StepPanelProps = {
  step: number;
  offer?: Offer;
  handleUserDeletion?: CreateInviteProviderProps['handleUserDeletion'];
  onBack?: () => void;
};

const StepPanel: React.FC<StepPanelProps> = ({ step, offer, handleUserDeletion, onBack }) => {
  if (
    offer &&
    (offer?.category === OfferCategory.event || offer?.category === OfferCategory.product)
  ) {
    switch (step) {
      case EventStep.OfferSelection:
        return <Step1 onBack={onBack} />;
      case EventStep.RequirementSelection:
        return <RequirementStep />;
      case EventStep.Summary:
        return <Step3 handleUserDeletion={handleUserDeletion} />;
    }
  } else {
    switch (step) {
      case Step.OfferSelection:
        return <Step1 onBack={onBack} />;
      case Step.DateSelection:
        return <Step2 />;
      case Step.RequirementSelection:
        return <RequirementStep />;
      case Step.Summary:
        return <Step3 handleUserDeletion={handleUserDeletion} />;
    }
  }
  return null;
};

export const InviteStepsContainer: React.FC<{
  handleUserDeletion?: CreateInviteProviderProps['handleUserDeletion'];
  onBack?: () => void;
}> = ({ handleUserDeletion, onBack }) => {
  const { state } = useCreateInvite();

  const renderSteps = () => {
    if (
      (state.offer?.category === OfferCategory.event ||
        state.offer?.category === OfferCategory.product) &&
      state.step === 3
    ) {
      return (
        <Box p={'24px 14px 0px 96px'}>
          <SentSuccessfully onCollabBack={onBack} />
        </Box>
      );
    }

    if (state.step === Step.Success) {
      return (
        <Box p={'34px 20px 0px 146px'}>
          <SentSuccessfully onCollabBack={onBack} />
        </Box>
      );
    }

    return (
      <StepPanel
        step={state.step}
        offer={state.offer}
        handleUserDeletion={handleUserDeletion}
        onBack={onBack}
      />
    );
  };

  return (
    <Grid container>
      <Grid item xs={8}>
        {renderSteps()}
      </Grid>
      <Grid item xs={4}>
        <Confirmation />
      </Grid>
    </Grid>
  );
};

export const CollabInvite: React.FC<Props> = ({ reducer, handleUserDeletion, onBack }) => {
  const [collabState, dispatch] = reducer;
  const {
    selected: { users: selectedUsers },
  } = collabState;

  const appDispatch = useAppDispatch();
  const uniqueUsers = React.useMemo(
    () => Array.from(new Map(selectedUsers.map((user) => [user.id, user])).values()),
    [selectedUsers],
  );

  return (
    <Box>
      <CreateInviteProvider
        users={uniqueUsers}
        onSuccess={() => {
          appDispatch(markHighlight(true));
          dispatch({ type: 'RESET_SELECTED_USERS' });
        }}
      >
        <InviteStepsContainer handleUserDeletion={handleUserDeletion} onBack={onBack} />
      </CreateInviteProvider>
    </Box>
  );
};
